import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Buildings, CalendarBlank, Check, Coins, Plus, Tag, Trash, TreeStructure, X } from '@phosphor-icons/react';
import {
  BestaetigenDialog, FeldRaster, ImmoButton, InnenKarte, KartenTitel, PanelZeile, SchalterZeile, SearchSelect, SegmentedControl, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { objektName } from '../../../../../lib/immobilien/objektKopf';
import { entferneObjektLink } from '../../../../../lib/dienstleister/api';
import { ordneObjektZu, setzeKategorien } from '../../../../../lib/dienstleister/aktionen';
import { istVertrag, zuordnungLaeuft } from '../../../../../lib/dienstleister/liste';
import { zahl } from '../../../../../lib/dienstleister/neuerDienstleister';
import { meldeDlGeaendert } from '../../../../../lib/dienstleister/useDienstleisterDetail';
import { useDlSeite } from '../DienstleisterSeitenKontext';
import { fehlerText, GLAS } from './teile';

export const VERTRAGSARTEN = ['Einzelauftrag', 'Rahmenvertrag', 'Wartungsvertrag', 'Hausmeistervertrag', 'Reinigungsvertrag'] as const;
type Vertragsart = typeof VERTRAGSARTEN[number];

// ── Objekt-Zuordnung (Screen 20) ───────────────────────────────────────────────
export function ObjektePanel() {
  const s = useDlSeite();
  const d = s.daten.dienstleister;
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState(false);
  const [suchen, setSuchen] = useState(false);
  const [objektId, setObjektId] = useState<string | null>(null);
  const [art, setArt] = useState<Vertragsart>('Rahmenvertrag');
  const [von, setVon] = useState(s.heute);
  const [bis, setBis] = useState('');
  const [kosten, setKosten] = useState('');
  const [umlage, setUmlage] = useState(false);
  const [entfernen, setEntfernen] = useState<{ id: string; name: string } | null>(null);
  const obj = useMemo(() => new Map(s.daten.objekte.map(o => [o.id, o])), [s.daten.objekte]);
  const name = (id: string) => { const o = obj.get(id); return o ? objektName(o) : 'Objekt'; };
  const zuordnungen = [...s.daten.zuordnungen].sort((a, b) => Number(zuordnungLaeuft(b, s.heute)) - Number(zuordnungLaeuft(a, s.heute)) || name(a.objekt_id).localeCompare(name(b.objekt_id), 'de'));
  const laufend = zuordnungen.filter(z => zuordnungLaeuft(z, s.heute));
  const vorhanden = new Set(s.daten.zuordnungen.map(z => z.objekt_id));
  const optionen = useMemo(() => s.daten.objekte.map(o => ({
    id: o.id, label: objektName(o), sub: [o.objektnummer, (o.stadt ?? o.ort ?? '').trim()].filter(Boolean).join(' · ') + (vorhanden.has(o.id) ? ' · bereits zugeordnet' : ''),
    suchtext: [o.objektnummer, o.strasse, o.hausnummer, o.stadt, o.ort].filter(Boolean).join(' '),
  })), [s.daten.objekte, vorhanden]);
  const kostenZahl = zahl(kosten);
  const kostenFehler = kostenZahl !== null && (Number.isNaN(kostenZahl) || kostenZahl < 0) ? 'Zahl ≥ 0' : undefined;
  const datumFehler = bis && von && bis < von ? 'Ende liegt vor dem Beginn' : undefined;
  const ausgewaehlt = objektId ? optionen.find(o => o.id === objektId) ?? null : null;
  const zuruecksetzen = () => { setSuchen(false); setObjektId(null); setArt('Rahmenvertrag'); setVon(s.heute); setBis(''); setKosten(''); setUmlage(false); };

  const zuordnen = async () => {
    if (!objektId || kostenFehler || datumFehler) return;
    setLaeuft(true);
    try {
      await ordneObjektZu(d.id, objektId, { von, bis, vertragsart: art, kostenJahr: istVertrag(art) && kostenZahl !== null && !Number.isNaN(kostenZahl) ? kostenZahl : null, umlagefaehig: istVertrag(art) && umlage });
      zeige(`${name(objektId)} zugeordnet`);
      zuruecksetzen();
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Nicht zugeordnet: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  const bestaetigeEntfernen = async () => {
    if (!entfernen) return;
    setLaeuft(true);
    try { await entferneObjektLink(d.id, entfernen.id); zeige(`Zuordnung ${entfernen.name} entfernt`); setEntfernen(null); meldeDlGeaendert(d.id); }
    catch (err) { zeige(`Nicht entfernt: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {entfernen && (
        <BestaetigenDialog titel={`Zuordnung ${entfernen.name} entfernen?`} text="Die Objekt-Zuordnung wird gelöscht. Soll nur ein Vertrag enden, stattdessen unter „Verträge“ kündigen."
          bestaetigenLabel="Entfernen" laeuft={laeuft} onAbbrechen={() => setEntfernen(null)} onBestaetigen={() => { void bestaetigeEntfernen(); }} />
      )}
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}>
          <KartenTitel rechts={!suchen ? <ImmoButton groesse={36} icon={Plus} onClick={() => setSuchen(true)}>Objekt zuordnen</ImmoButton> : undefined}>
            {`${laufend.length} ${laufend.length === 1 ? 'Objekt-Zuordnung' : 'Objekt-Zuordnungen'}`}
          </KartenTitel>
        </div>
        {zuordnungen.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Keine Objekt-Zuordnung</span>}
        {zuordnungen.map(z => {
          const o = obj.get(z.objekt_id);
          const laeuftNoch = zuordnungLaeuft(z, s.heute);
          const artText = (z.vertragsart ?? '').trim() || 'Zuordnung';
          const sub = [artText, z.von ? `seit ${deDatum(z.von)}` : '', z.bis ? `bis ${deDatum(z.bis)}` : '', (o?.stadt ?? o?.ort ?? '').trim(), laeuftNoch ? '' : 'beendet'].filter(Boolean).join(' · ');
          const titel = [o?.objektnummer, name(z.objekt_id)].filter(Boolean).join(' · ');
          return (
            <div key={z.objekt_id} className="flex items-center min-w-0" style={{ gap: 4 }}>
              <div className="flex-1 min-w-0">
                <PanelZeile icon={Buildings} ton={laeuftNoch ? 'acc' : 'neutral'} aktiv={laeuftNoch} titel={titel} sub={sub}
                  chip={{ text: artText, ton: istVertrag(z.vertragsart) ? 'info' : 'neutral' }} onClick={() => s.zumObjekt(z.objekt_id)} ariaLabel={`${name(z.objekt_id)} öffnen`} />
              </div>
              <ImmoButton groesse={32} icon={Trash} aria-label={`Zuordnung ${name(z.objekt_id)} entfernen`} title="Zuordnung entfernen" onClick={() => setEntfernen({ id: z.objekt_id, name: name(z.objekt_id) })} />
            </div>
          );
        })}
      </InnenKarte>

      {suchen && (
        <InnenKarte style={GLAS}>
          <KartenTitel>{ausgewaehlt ? `Zuordnen: ${ausgewaehlt.label}` : 'Objekt wählen …'}</KartenTitel>
          <SearchSelect label="Objekt suchen" optionen={optionen} ausgewaehlt={ausgewaehlt} onWahl={o => setObjektId(o.id)} onEntfernen={() => setObjektId(null)}
            platzhalter="Objektname, Nr. oder Ort" autoFocus />
          {ausgewaehlt && vorhanden.has(ausgewaehlt.id) && <span style={{ fontSize: 12, color: 'var(--warn)' }}>Bereits zugeordnet – Zuordnen überschreibt Vertragsart und Zeitraum.</span>}
          {ausgewaehlt && (
            <>
              <SegmentedControl<Vertragsart> ariaLabel="Vertragsart" hoehe={34} className="self-start" wert={art} onChange={setArt} segmente={VERTRAGSARTEN.map(v => ({ key: v, label: v }))} />
              <FeldRaster minBreite={180}>
                <TextFeld label="von" icon={CalendarBlank} type="date" wert={von} onChange={setVon} />
                <TextFeld label="bis (optional)" icon={CalendarBlank} type="date" wert={bis} onChange={setBis} fehler={datumFehler} />
                {istVertrag(art) && <TextFeld label="Kosten / Jahr" icon={Coins} inputMode="decimal" suffix="€" wert={kosten} onChange={setKosten} fehler={kostenFehler} />}
              </FeldRaster>
              {istVertrag(art) && <SchalterZeile an={umlage} onChange={setUmlage} label="Umlagefähig (Betriebskosten)" />}
            </>
          )}
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <ImmoButton icon={X} disabled={laeuft} onClick={zuruecksetzen}>Abbrechen</ImmoButton>
            {ausgewaehlt && <ImmoButton art="akzent" icon={Check} disabled={laeuft || !!kostenFehler || !!datumFehler} onClick={() => { void zuordnen(); }}>{laeuft ? 'Wird zugeordnet …' : 'Zuordnen'}</ImmoButton>}
          </div>
        </InnenKarte>
      )}
    </>
  );
}

// ── Gewerke & Kategorien (Screen 19) ───────────────────────────────────────────
export function GewerkePanel() {
  const s = useDlSeite();
  const navigate = useNavigate();
  const d = s.daten.dienstleister;
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState(false);
  const gewaehlt = s.daten.kategorieIds;
  const sichtbar = s.daten.kategorien.filter(k => k.aktiv || gewaehlt.includes(k.id));
  const schalte = async (id: string, name: string, an: boolean) => {
    if (!an && gewaehlt.length <= 1) { zeige('Mindestens eine Kategorie nötig', true); return; }
    setLaeuft(true);
    try {
      await setzeKategorien(d.id, gewaehlt, an ? [...gewaehlt, id] : gewaehlt.filter(x => x !== id));
      zeige(`${name} ${an ? 'hinzugefügt' : 'entfernt'}`);
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Nicht gespeichert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS} gap={4}>
        <KartenTitel>Gewerke steuern die Ticket-Zuweisung</KartenTitel>
        <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
          Erkennt eine Regel z. B. „Wasserschaden“, wird ein Dienstleister der passenden Kategorie vorgeschlagen – bevorzugte zuerst, bei Notfall nur Notfallkontakte.
        </span>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel rechts={<ImmoButton groesse={36} icon={TreeStructure} onClick={() => navigate('/admin/dienstleister?kategorien=1')}>Kategorien verwalten</ImmoButton>}>
          {`${gewaehlt.length} ${gewaehlt.length === 1 ? 'Gewerk' : 'Gewerke'} ausgewählt`}
        </KartenTitel>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 6 }}>
          {sichtbar.map(k => (
            <SchalterZeile key={k.id} an={gewaehlt.includes(k.id)} disabled={laeuft} icon={Tag} klein
              label={`${k.name}${k.ist_handwerk ? ' · Handwerk' : ''}${k.aktiv ? '' : ' · inaktiv'}`}
              onChange={an => { void schalte(k.id, k.name, an); }} />
          ))}
        </div>
      </InnenKarte>
    </>
  );
}
